import { PauseOctagon, Play } from 'lucide-react'
import { useState } from 'react'
import { useReadOnlyState, useSetReadOnly } from '@/hooks/useOps'
import { useSession } from '@/lib/session'
import { Button } from '@/ui/Button'
import { CardBody, CardHeader, Housing } from '@/ui/Card'
import { Badge, Callout, Modal, Skeleton } from '@/ui/primitives'
import { StatBlock } from '@/ui/readout'

/**
 * The kill switch.
 *
 * Flips a Redis flag that every write-implying endpoint checks, degrading the whole
 * product to view-only in under ten seconds with no deploy. On-chain positions are
 * untouched — this only stops the app from encouraging new transactions.
 *
 * ── Why it gets a whole page ──
 * It is one boolean, and it could have been a toggle in the header. It is not, because
 * the cost of an accidental flip is the entire product going dark, and a control that
 * lives beside things people click casually will eventually be clicked casually. A page
 * you have to navigate to, with a reason you have to write and a confirmation you have
 * to read, is the friction this particular boolean is worth.
 */
export function KillSwitch() {
  const { session } = useSession()
  const readOnly = useReadOnlyState()
  const flip = useSetReadOnly()

  const [reason, setReason] = useState('')
  const [confirming, setConfirming] = useState(false)

  // The server enforces 3–500 characters. Mirrored so the operator finds out before the
  // round trip rather than after it.
  const reasonValid = reason.trim().length >= 3 && reason.trim().length <= 500
  const isOn = readOnly.data === true
  // What the button will DO, which is always the opposite of the current state.
  const nextState = !isOn

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="display display-tight text-3xl">Kill switch</h1>
        <p className="mt-1.5 text-sm text-muted">
          Degrades the product to view-only across every client, immediately, with no deploy.
        </p>
      </header>

      <Housing>
        <CardHeader
          title="Current state"
          action={
            readOnly.isLoading ? (
              <Skeleton className="h-6 w-20" />
            ) : isOn ? (
              <Badge tone="danger">Read-only</Badge>
            ) : (
              <Badge tone="positive">Live</Badge>
            )
          }
        />
        <CardBody className="pt-4">
          {readOnly.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <StatBlock
              label="Protocol"
              value={isOn ? 'Halted' : 'Accepting writes'}
              size="sm"
              tone={isOn ? 'fault' : 'fluid'}
              note={
                isOn
                  ? 'Every write-implying endpoint is refusing. Reads, balances and existing positions are unaffected.'
                  : 'Bonds, deposits, withdrawals and settlement are all open.'
              }
            />
          )}
        </CardBody>
      </Housing>

      <Housing>
        <CardHeader
          title={nextState ? 'Halt the protocol' : 'Resume the protocol'}
          subtitle="The reason is written to an append-only audit table alongside your address. It is the only record an incident review will have."
        />
        <CardBody className="space-y-4 pt-4">
          <label className="block">
            <span className="label">Reason</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={
                nextState
                  ? 'What is happening that requires halting writes.'
                  : 'What was resolved, and why it is safe to resume.'
              }
              className="surface-glass-2 mt-2 w-full resize-y rounded-[10px] px-3 py-2 text-sm text-ink ring-1 ring-line outline-none placeholder:text-faint focus:ring-accent-edge"
            />
            <span className="mt-1.5 block text-[10px] text-faint">
              Required, 3–500 characters. Acting as{' '}
              <span className="tnum">{session?.address ?? '—'}</span>.
            </span>
          </label>

          <Button
            variant={nextState ? 'danger' : 'primary'}
            fullWidth
            disabled={!reasonValid || readOnly.isLoading}
            onClick={() => setConfirming(true)}
            iconLeft={
              nextState ? (
                <PauseOctagon className="size-4" aria-hidden />
              ) : (
                <Play className="size-4" aria-hidden />
              )
            }
          >
            {nextState ? 'Halt writes' : 'Resume writes'}
          </Button>

          {flip.isSuccess && (
            <Callout tone="info" title="Done">
              The switch is now {flip.data.readOnly ? 'ON — the product is read-only' : 'OFF — writes are open'}.
              Clients pick this up within about ten seconds.
            </Callout>
          )}

          {flip.error && (
            <Callout tone="danger" title="Request failed">
              {(flip.error as Error).message}
            </Callout>
          )}
        </CardBody>
      </Housing>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={nextState ? 'Halt the protocol?' : 'Resume the protocol?'}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant={nextState ? 'danger' : 'primary'}
              loading={flip.isPending}
              onClick={() =>
                void flip
                  .mutateAsync({ enabled: nextState, reason: reason.trim() })
                  .then(() => {
                    setConfirming(false)
                    setReason('')
                  })
                  .catch(() => setConfirming(false))
              }
            >
              {nextState ? 'Halt now' : 'Resume now'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-muted">
            {nextState ? (
              <>
                Every write-implying endpoint will start refusing within about ten seconds.
                Users keep their balances, positions and history — they simply cannot bond,
                deposit, withdraw or settle until this is turned back off.
              </>
            ) : (
              <>
                Writes reopen across every client within about ten seconds. Make sure whatever
                caused the halt is actually resolved: there is no staged rollout here, and the
                first thing that arrives will be the backlog.
              </>
            )}
          </p>

          <div className="surface-glass-2 rounded-[10px] px-4 py-3 ring-1 ring-line">
            <p className="label">Reason to be recorded</p>
            <p className="mt-1.5 text-sm text-ink">{reason.trim()}</p>
          </div>
        </div>
      </Modal>
    </div>
  )
}
